import React from 'react';

const TermsOfUsePage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-saverly-primary/10 to-saverly-deep/10 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-saverly-dark font-poppins mb-6">
            Terms of Use
          </h1>
          <p className="text-lg text-saverly-gray font-inter">
            Last Updated: December 7, 2025
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            
            <p className="text-saverly-gray font-inter leading-relaxed mb-4">
              These Terms of Use ("Terms") govern your access to and use of the Saverly mobile application and related websites, tools, and services (collectively, the "Services"). By accessing or using the Services, you agree to be bound by these Terms. If you do not agree, you may not use the Services.
            </p>
            <p className="text-saverly-gray font-inter leading-relaxed mb-12">
              We may update these Terms at any time. Continued use of the Services after changes are posted constitutes acceptance of the revised Terms.
            </p>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-4">1. Description of Services</h2>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                Saverly provides AI-powered meal planning and smart grocery shopping tools, including:
              </p>
              <ul className="list-disc list-inside text-saverly-gray font-inter space-y-2 ml-4 mb-4">
                <li>Browsing real-time product sales and promotions from Kroger</li>
                <li>AI-generated meal plans and recipe suggestions</li>
                <li>Ingredient-to-product matching</li>
                <li>Shopping list creation and management</li>
                <li>Product information such as pricing, nutrition, and availability</li>
                <li>Store lookup and related retailer information</li>
              </ul>
              <p className="text-saverly-gray font-inter leading-relaxed">
                <strong>Saverly does not sell groceries, process retail transactions, or handle payments for any retailer.</strong> All purchases occur solely and directly through Kroger or other retailers.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-4">2. User Accounts</h2>
              
              <h3 className="text-xl font-bold text-saverly-dark font-poppins mb-3 mt-6">2.1 Account Creation</h3>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                To access certain features, you must create an account. You agree to:
              </p>
              <ul className="list-disc list-inside text-saverly-gray font-inter space-y-2 mb-4 ml-4">
                <li>Provide accurate and up-to-date information</li>
                <li>Maintain the confidentiality of your login credentials</li>
                <li>Notify us immediately of any unauthorized access</li>
                <li>Be responsible for all activity under your account</li>
              </ul>

              <h3 className="text-xl font-bold text-saverly-dark font-poppins mb-3 mt-6">2.2 Eligibility</h3>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                You must be at least 13 years old to use Saverly. If you are under 18, you may use the Services only with parental or guardian consent.
              </p>
              <p className="text-saverly-gray font-inter leading-relaxed">
                We do not knowingly collect information from children under 13. If we learn that we have done so, we will delete such information promptly.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-4">3. Acceptable Use</h2>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                You agree not to:
              </p>
              <ul className="list-disc list-inside text-saverly-gray font-inter space-y-2 ml-4">
                <li>Use the Services for unlawful activities</li>
                <li>Attempt unauthorized access to any systems or accounts</li>
                <li>Interfere with or disrupt the Services</li>
                <li>Use bots, scrapers, or automated tools without permission</li>
                <li>Reverse engineer or extract source code</li>
                <li>Copy, modify, or distribute any part of the Services</li>
                <li>Upload, transmit, or distribute malware or harmful code</li>
                <li>Impersonate any person or entity</li>
                <li>Harass, threaten, or abuse others</li>
                <li>Collect or store personal information about other users</li>
              </ul>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-4">4. AI-Generated Content</h2>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                Saverly uses artificial intelligence to generate meal plans, ingredient suggestions, and recipes. You acknowledge that:
              </p>
              <ul className="list-disc list-inside text-saverly-gray font-inter space-y-2 mb-4 ml-4">
                <li>AI-generated content is informational only</li>
                <li>Meal plans may not meet your dietary, nutritional, or medical needs</li>
                <li>Recipes may contain inaccuracies or incomplete information</li>
                <li>You are solely responsible for verifying suitability for your health, diet, and allergies</li>
                <li>You should consult a healthcare provider or nutrition professional before making dietary changes</li>
              </ul>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                <strong>Saverly is not a medical or nutritional service and does not provide medical advice, diagnosis, or treatment.</strong>
              </p>
              <p className="text-saverly-gray font-inter leading-relaxed">
                You may not reproduce or commercially distribute AI-generated content without our written permission.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-4">5. Product Information and Pricing</h2>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                Product details, prices, and promotions are provided through the Kroger API. You acknowledge that:
              </p>
              <ul className="list-disc list-inside text-saverly-gray font-inter space-y-2 mb-4 ml-4">
                <li>Pricing, availability, and promotions may change at any time</li>
                <li>Displayed prices may differ from in-store prices</li>
                <li>Saverly does not guarantee product accuracy, availability, or savings</li>
                <li>Final pricing is determined exclusively by the retailer</li>
              </ul>
              <p className="text-saverly-gray font-inter leading-relaxed">
                <strong>Saverly does not process purchases. All orders are processed directly by Kroger.</strong>
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-4">6. Subscriptions, Billing & Refunds</h2>
              
              <h3 className="text-xl font-bold text-saverly-dark font-poppins mb-3 mt-6">6.1 Plans</h3>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                Saverly may offer free and paid subscription tiers. Paid plans provide additional features as described in the Pricing section.
              </p>

              <h3 className="text-xl font-bold text-saverly-dark font-poppins mb-3 mt-6">6.2 Billing</h3>
              <ul className="list-disc list-inside text-saverly-gray font-inter space-y-2 mb-4 ml-4">
                <li>Fees are billed in advance on a recurring monthly or annual basis</li>
                <li>You authorize Saverly to charge your selected payment method</li>
                <li>Prices may change with advance notice</li>
                <li>Failed payments may result in suspension of service</li>
              </ul>

              <h3 className="text-xl font-bold text-saverly-dark font-poppins mb-3 mt-6">6.3 Free Trials</h3>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                At the end of a free trial, your plan will automatically convert to a paid subscription unless canceled before the trial ends.
              </p>

              <h3 className="text-xl font-bold text-saverly-dark font-poppins mb-3 mt-6">6.4 Cancellation & Refunds</h3>
              <ul className="list-disc list-inside text-saverly-gray font-inter space-y-2 ml-4">
                <li>You may cancel anytime through your account settings</li>
                <li>Cancellation takes effect at the end of the billing period</li>
                <li>First-time subscribers may request a refund within 30 days of initial purchase</li>
                <li>Refunds are credited to the original payment method</li>
                <li>No refunds are provided for partial billing periods or unused time</li>
              </ul>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-4">7. Intellectual Property</h2>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                All content, trademarks, graphics, logos, software, and materials provided through the Services are the exclusive property of Saverly or our licensors.
              </p>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                You are granted a limited, non-exclusive, non-transferable license to use the Services for personal, non-commercial purposes.
              </p>
              <p className="text-saverly-gray font-inter leading-relaxed">
                You may not copy, modify, distribute, or create derivative works from the Services without written permission.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-4">8. User-Generated Content</h2>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                You retain ownership of content you create within the Services (such as shopping lists or saved meal plans).
              </p>
              <p className="text-saverly-gray font-inter leading-relaxed">
                By using the Services, you grant Saverly a non-exclusive, worldwide, royalty-free license to use and display your content solely for purposes of operating and improving the Services.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-4">9. Third-Party Services</h2>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                The Services rely on third-party providers, including:
              </p>
              <ul className="list-disc list-inside text-saverly-gray font-inter space-y-2 mb-4 ml-4">
                <li>Kroger API for product data</li>
                <li>OpenAI for AI-generated content</li>
                <li>Clerk for user authentication</li>
                <li>Convex for backend data operations</li>
              </ul>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                Your use of these services may be subject to their respective terms and privacy policies.
              </p>
              <p className="text-saverly-gray font-inter leading-relaxed">
                Saverly is not responsible for:
              </p>
              <ul className="list-disc list-inside text-saverly-gray font-inter space-y-2 ml-4">
                <li>Outages, interruptions, or rate limits imposed by third-party APIs</li>
                <li>Errors or inaccuracies in third-party data</li>
                <li>Actions or policies of third-party providers</li>
              </ul>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-4">10. App Store Compliance</h2>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                If you download Saverly from the Apple App Store or Google Play Store, you acknowledge that:
              </p>
              <ul className="list-disc list-inside text-saverly-gray font-inter space-y-2 mb-4 ml-4">
                <li>Your use must comply with the store's applicable terms</li>
                <li>Apple and Google are not responsible for the operation of the Services</li>
                <li>Apple and Google provide no warranties and have no obligation to provide support</li>
              </ul>
              <p className="text-saverly-gray font-inter leading-relaxed">
                You agree that Saverly, not Apple or Google, is responsible for addressing any claims relating to the app.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-4">11. Data Retention and Account Deletion</h2>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                We retain and process your data as described in our Privacy Policy.
              </p>
              <p className="text-saverly-gray font-inter leading-relaxed">
                You may request deletion of your account and associated data at any time, and we will comply unless retention is required by law or legitimate business needs.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-4">12. Disclaimers</h2>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND. TO THE FULLEST EXTENT PERMITTED BY LAW, SAVERLY DISCLAIMS ALL EXPRESS OR IMPLIED WARRANTIES, INCLUDING:
              </p>
              <ul className="list-disc list-inside text-saverly-gray font-inter space-y-2 mb-4 ml-4">
                <li>Merchantability or fitness for a particular purpose</li>
                <li>Accuracy, reliability, or completeness of AI-generated content</li>
                <li>Availability or uninterrupted operation of the Services</li>
                <li>Accuracy of product listings, pricing, or promotions</li>
                <li>Nutritional accuracy of recipes or meal plans</li>
              </ul>
              <p className="text-saverly-gray font-inter leading-relaxed">
                Saverly makes no guarantee of savings, dietary suitability, or health outcomes.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-4">13. Limitation of Liability</h2>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, SAVERLY SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR GOODWILL.
              </p>
              <p className="text-saverly-gray font-inter leading-relaxed">
                IN NO EVENT SHALL SAVERLY'S TOTAL LIABILITY EXCEED:
              </p>
              <ul className="list-disc list-inside text-saverly-gray font-inter space-y-2 ml-4">
                <li>(a) the amount paid by you in the preceding 12 months, OR</li>
                <li>(b) one hundred dollars (US $100), whichever is greater.</li>
              </ul>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-4">14. Indemnification</h2>
              <p className="text-saverly-gray font-inter leading-relaxed">
                You agree to indemnify and hold harmless Saverly, its officers, employees, contractors, and agents from any claims or damages arising out of:
              </p>
              <ul className="list-disc list-inside text-saverly-gray font-inter space-y-2 ml-4">
                <li>Your use of the Services</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any third-party rights</li>
              </ul>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-4">15. Termination</h2>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                We may suspend or terminate your account at any time for:
              </p>
              <ul className="list-disc list-inside text-saverly-gray font-inter space-y-2 mb-4 ml-4">
                <li>Violations of these Terms</li>
                <li>Fraudulent or abusive behavior</li>
                <li>Prolonged inactivity</li>
                <li>Failure to pay fees</li>
              </ul>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                Upon termination, your right to use the Services ceases immediately.
              </p>
              <p className="text-saverly-gray font-inter leading-relaxed">
                We may delete your data unless retention is required by law.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-4">16. Governing Law & Dispute Resolution</h2>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                These Terms are governed by the laws of the United States and the state in which Saverly operates, without regard to conflict-of-law principles.
              </p>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                Disputes will be resolved by binding arbitration under the rules of the American Arbitration Association. Either party may bring individual claims in small claims court.
              </p>
              <p className="text-saverly-gray font-inter leading-relaxed">
                You waive the right to participate in class actions or class arbitration.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-4">17. Changes to Terms</h2>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                We may modify these Terms at any time. Updated Terms will be posted with a revised "Last Updated" date.
              </p>
              <p className="text-saverly-gray font-inter leading-relaxed">
                Continued use of the Services after changes constitutes acceptance.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-4">18. Contact Us</h2>
              <p className="text-saverly-gray font-inter leading-relaxed mb-4">
                For questions about these Terms, contact us:
              </p>
              <ul className="list-none text-saverly-gray font-inter space-y-2">
                <li><strong>Email:</strong> <a href="mailto:legal@saverly.com" className="text-saverly-primary hover:text-saverly-deep underline">legal@saverly.com</a></li>
                <li><strong>Email:</strong> <a href="mailto:support@saverly.com" className="text-saverly-primary hover:text-saverly-deep underline">support@saverly.com</a></li>
              </ul>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfUsePage;

